import { resourcesCopy } from '@/copy/resources'
import {
  CheckboxField,
  SelectField,
  TextareaField,
  TextField,
} from '@/ui/Field/Field'
import { Text } from '@/ui/layout/Text'
import { ApiError } from '../../api/client'
import { githubAppApi, postManifestToGitHub } from '../../api/githubApp'
import type { ResourceScope } from '../../api/resources'
import { startCentralInstall } from '../../utils/githubInstall'
import {
  type FieldProps,
  type Fields,
  fieldStr,
  preservedKeys,
  type ResourceFormDescriptor,
  STORED,
  storedData,
} from './shared'

const DEFAULT_TOOLSETS = 'repos,pull_requests,issues'

/** One-click create mode value — never persisted; intercepted by
 * `githubForm.createVia` before `toBody` runs. */
const AUTH_ONE_CLICK = 'app_click'

/** Central-App install mode value — the default when the instance has a
 * vendor-registered App configured. */
const AUTH_INSTALL = 'app_install'

/** Optional app-mode keys the form owns: a blank input clears them, so in
 * app mode they must not ride along with the preserved stored keys. */
const FORM_OPTIONAL_KEYS = new Set(['repositories', 'web_url', 'app_slug'])

/**
 * The github connection form:
 *
 * - install (default on create): the instance's central GitHub App —
 *   chromaframe sends you to GitHub's install page where you pick the
 *   account/org. Nothing to name or type.
 * - one-click own App: chromaframe creates a GitHub App from a manifest
 *   (self-hosted / GHES / org-owned Apps), connects it, and sends you on
 *   to install it.
 * - manual GitHub App: app id, optional installation id, PEM key.
 * - PAT: legacy token/toolsets/read-only against the hosted MCP server.
 */
// eslint-disable-next-line react-refresh/only-export-components -- local component; the descriptor below is this file's only export
function GitHubFields({ fields, set, isEdit }: FieldProps) {
  const auth = fields.auth || (isEdit ? 'pat' : AUTH_INSTALL)
  return (
    <>
      <SelectField
        id="res-gh-auth"
        label={resourcesCopy.forms.github.authMode}
        value={auth}
        onValue={(v) => set('auth', v)}
      >
        {!isEdit && (
          <option value={AUTH_INSTALL}>
            {resourcesCopy.forms.github.authInstall}
          </option>
        )}
        {!isEdit && (
          <option value={AUTH_ONE_CLICK}>
            {resourcesCopy.forms.github.authOwnApp}
          </option>
        )}
        <option value="app">{resourcesCopy.forms.github.authApp}</option>
        <option value="pat">{resourcesCopy.forms.github.authPat}</option>
      </SelectField>

      {auth === AUTH_INSTALL ? null : auth === AUTH_ONE_CLICK ? (
        <>
          <TextField
            id="res-gh-org"
            label={resourcesCopy.forms.github.organization}
            hint={resourcesCopy.form.optional}
            help={resourcesCopy.forms.github.organizationHelp}
            value={fields.organization ?? ''}
            onValue={(v) => set('organization', v)}
            inputProps={{
              placeholder: resourcesCopy.forms.github.organizationPlaceholder,
            }}
          />
          <TextField
            id="res-gh-oneclick-web-url"
            label={resourcesCopy.forms.github.enterpriseHost}
            hint={resourcesCopy.form.optional}
            help={resourcesCopy.forms.github.hostHelp}
            value={fields.web_url ?? ''}
            onValue={(v) => set('web_url', v)}
            inputProps={{
              placeholder: resourcesCopy.forms.github.hostPlaceholder,
            }}
          />
        </>
      ) : auth === 'app' && isCentral(fields) ? (
        <>
          <Text as="p" variant="caption" tone="text-mute">
            {resourcesCopy.forms.github.central}
          </Text>
          <RepositoriesField fields={fields} set={set} />
        </>
      ) : auth === 'app' ? (
        <>
          <TextField
            id="res-gh-app-id"
            label={resourcesCopy.forms.github.appId}
            value={fields.app_id ?? ''}
            onValue={(v) => set('app_id', v)}
            inputProps={{
              placeholder: resourcesCopy.forms.github.appIdPlaceholder,
            }}
          />
          <TextField
            id="res-gh-app-slug"
            label={resourcesCopy.forms.github.appSlug}
            help={resourcesCopy.forms.github.appSlugHelp}
            value={fields.app_slug ?? ''}
            onValue={(v) => set('app_slug', v)}
            inputProps={{
              placeholder: resourcesCopy.forms.github.appSlugPlaceholder,
            }}
          />
          <TextField
            id="res-gh-installation"
            label={resourcesCopy.forms.github.installationId}
            hint={resourcesCopy.forms.github.installationHint}
            value={fields.installation_id ?? ''}
            onValue={(v) => set('installation_id', v)}
            inputProps={{
              placeholder: resourcesCopy.forms.github.installationPlaceholder,
            }}
          />
          <TextareaField
            id="res-gh-private-key"
            label={
              isEdit
                ? resourcesCopy.forms.github.privateKeyKeep
                : resourcesCopy.forms.github.privateKey
            }
            hint={isEdit ? resourcesCopy.form.leaveBlankToKeep : undefined}
            value={fields.private_key ?? ''}
            onValue={(v) => set('private_key', v)}
            textareaProps={{
              placeholder: resourcesCopy.forms.github.privateKeyPlaceholder,
              spellCheck: false,
            }}
          />
          <TextField
            id="res-gh-web-url"
            label={resourcesCopy.forms.github.webUrl}
            hint={resourcesCopy.form.optional}
            help={resourcesCopy.forms.github.hostHelp}
            value={fields.web_url ?? ''}
            onValue={(v) => set('web_url', v)}
            inputProps={{
              placeholder: resourcesCopy.forms.github.hostPlaceholder,
            }}
          />
          <RepositoriesField fields={fields} set={set} />
          <Text as="p" variant="caption" tone="text-mute">
            {resourcesCopy.forms.github.afterSave}
          </Text>
          <CheckboxField
            id="res-gh-reconcile"
            label={resourcesCopy.forms.github.redeliver}
            checked={fields.reconcile !== 'false'}
            onChecked={(b) => set('reconcile', String(b))}
          />
          <CheckboxField
            id="res-gh-allow-approve"
            label={resourcesCopy.forms.github.allowApprove}
            checked={fields.allow_approve === 'true'}
            onChecked={(b) => set('allow_approve', String(b))}
          />
        </>
      ) : (
        <>
          <TextField
            id="res-gh-token"
            label={
              isEdit
                ? resourcesCopy.forms.github.tokenKeep
                : resourcesCopy.forms.github.token
            }
            hint={isEdit ? resourcesCopy.form.leaveBlankToKeep : undefined}
            value={fields.token ?? ''}
            onValue={(v) => set('token', v)}
            inputProps={{
              type: 'password',
              placeholder: resourcesCopy.forms.github.tokenPlaceholder,
            }}
          />
          <TextField
            id="res-toolsets"
            label={resourcesCopy.forms.github.toolsets}
            hint={resourcesCopy.forms.github.toolsetsHint}
            value={fields.toolsets ?? DEFAULT_TOOLSETS}
            onValue={(v) => set('toolsets', v)}
            inputProps={{ placeholder: DEFAULT_TOOLSETS }}
          />
          <CheckboxField
            id="res-read-only"
            label={resourcesCopy.forms.github.readOnly}
            checked={fields.read_only === 'true'}
            onChecked={(b) => set('read_only', String(b))}
          />
        </>
      )}
    </>
  )
}

// eslint-disable-next-line react-refresh/only-export-components -- local component; the descriptor below is this file's only export
function RepositoriesField({
  fields,
  set,
}: Pick<FieldProps, 'fields' | 'set'>) {
  return (
    <TextField
      id="res-gh-repositories"
      label={resourcesCopy.forms.github.repositories}
      hint={resourcesCopy.form.optional}
      help={resourcesCopy.forms.github.repositoriesHelp}
      value={fields.repositories ?? ''}
      onValue={(v) => set('repositories', v)}
      inputProps={{
        placeholder: resourcesCopy.forms.github.repositoriesPlaceholder,
      }}
    />
  )
}

/** Central rows carry server-managed credentials; the form hides them. */
function isCentral(f: Fields): boolean {
  return storedData(f).central === true
}

function splitList(s: string | undefined): string[] {
  return (s ?? '')
    .split(/[,\n]/)
    .map((x) => x.trim())
    .filter(Boolean)
}

export const githubForm: ResourceFormDescriptor = {
  Fields: GitHubFields,
  // Install and one-click name the connection after the account/App.
  namedByFlow: (f) =>
    (f.auth || AUTH_INSTALL) === AUTH_INSTALL || f.auth === AUTH_ONE_CLICK,
  createVia: async (f: Fields, scope: ResourceScope) => {
    const auth = f.auth || AUTH_INSTALL
    if (auth !== AUTH_INSTALL && auth !== AUTH_ONE_CLICK) return false
    // Validation errors throw so the form stays open with the message
    // rendered by ResourceFormContent's error line.
    if (scope.kind !== 'workspace')
      throw new Error(resourcesCopy.forms.github.needsWorkspace)

    if (auth === AUTH_INSTALL) {
      try {
        await startCentralInstall(scope.id)
        return true
      } catch (e) {
        // Instances without a central App (self-hosted/GHES) offer the
        // manifest flow instead — fall through to it.
        if (!(e instanceof ApiError) || e.status !== 422) throw e
      }
    }

    const flow = await githubAppApi.startFlow(
      scope.id,
      f.organization?.trim() || undefined,
      f.web_url?.trim() || undefined,
    )
    postManifestToGitHub(flow)
  },
  toBody: (f: Fields) => {
    const base = {
      ...preservedKeys(f),
      auth:
        f.auth === AUTH_ONE_CLICK || f.auth === AUTH_INSTALL
          ? 'app'
          : f.auth || 'pat',
      ...(f.url ? { url: f.url } : {}),
    }
    if (base.auth === 'app') {
      const repositories = splitList(f.repositories)
      const webUrl = f.web_url?.trim()
      const appSlug = f.app_slug?.trim()
      const kept = Object.fromEntries(
        Object.entries(base).filter(([k]) => !FORM_OPTIONAL_KEYS.has(k)),
      )
      return {
        ...kept,
        app_id: f.app_id ?? '',
        ...(f.installation_id ? { installation_id: f.installation_id } : {}),
        ...(f.private_key ? { private_key: f.private_key } : {}),
        ...(repositories.length > 0 ? { repositories } : {}),
        ...(webUrl ? { web_url: webUrl } : {}),
        ...(appSlug ? { app_slug: appSlug } : {}),
        reconcile: f.reconcile !== 'false',
        allow_approve: f.allow_approve === 'true',
      }
    }
    return {
      ...base,
      toolsets: f.toolsets || DEFAULT_TOOLSETS,
      read_only: f.read_only === 'true',
      ...(f.token ? { token: f.token } : {}),
    }
  },
  fromResource: (d: unknown) => {
    const r = (d ?? {}) as Record<string, unknown>
    return {
      [STORED]: JSON.stringify(r),
      auth: fieldStr(d, 'auth', 'pat') || 'pat',
      // Secrets never round-trip; their inputs stay blank on edit.
      app_id: fieldStr(d, 'app_id'),
      installation_id: fieldStr(d, 'installation_id'),
      private_key: '',
      toolsets: fieldStr(d, 'toolsets', DEFAULT_TOOLSETS),
      read_only: String(r.read_only === true),
      reconcile: String(r.reconcile !== false),
      allow_approve: String(r.allow_approve === true),
      url: fieldStr(d, 'url'),
      web_url: fieldStr(d, 'web_url'),
      app_slug: fieldStr(d, 'app_slug'),
      repositories: Array.isArray(r.repositories)
        ? r.repositories.map(String).join(', ')
        : '',
    }
  },
}
